// Theme and density (spec/dashboard.md section 2.1): light / dark / system, and comfortable / compact. Both are applied
// to the root element before the first paint (index.html, main.tsx) and on change; `system` is resolved here, so the
// stylesheet has one dark block, and it follows the OS while the page is open.
export type ThemeChoice = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';

const KEY = 'hue.theme';
const DENSITY_KEY = 'hue.density';

export function loadTheme(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

export function applyTheme(choice: ThemeChoice): void {
  document.documentElement.setAttribute('data-theme', effectiveTheme(choice));
}

export function saveTheme(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    // storage may be unavailable (private window); the choice then lasts for the page
  }
  applyTheme(choice);
}

/** The theme the page shows right now (resolving `system`). */
export function effectiveTheme(choice: ThemeChoice = loadTheme()): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Follows the OS while the choice is `system`; returns the stop function. */
export function watchSystemTheme(): () => void {
  if (typeof matchMedia !== 'function') return () => undefined;
  const mq = matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (loadTheme() === 'system') applyTheme('system');
  };
  mq.addEventListener?.('change', onChange);
  return () => mq.removeEventListener?.('change', onChange);
}

export function loadDensity(): Density {
  try {
    return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

export function applyDensity(density: Density): void {
  if (density === 'compact') document.documentElement.setAttribute('data-density', 'compact');
  else document.documentElement.removeAttribute('data-density');
}

export function saveDensity(density: Density): void {
  try {
    if (density === 'compact') localStorage.setItem(DENSITY_KEY, 'compact');
    else localStorage.removeItem(DENSITY_KEY);
  } catch {
    // see saveTheme
  }
  applyDensity(density);
}
